import React, { useEffect, useState, useRef } from "react";
import { getFacturas, createFactura, updateFactura } from "../api/facturas.js";
import { getVentas, createVenta, anularVenta } from "../api/ventas.js";
import { getPedidos } from "../api/pedidos.js";
import { getEstablecimientos } from "../api/establecimientos.js";
import { getClientes, createCliente } from "../api/clientes.js";
import { getProductos } from "../api/productos.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";
import ComprobanteVenta from "../components/ComprobanteVenta";

const FacturaPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [ventas, setVentas] = useState([]);
    const [filteredVentas, setFilteredVentas] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [ventaCreada, setVentaCreada] = useState(null); 
    const [ventaParaImprimir, setVentaParaImprimir] = useState(null);
    const [clientes, setClientes] = useState([]);
    const [productos, setProductos] = useState([]);
    const [establecimientos, setEstablecimientos] = useState([]);
    const [restaurantes, setRestaurantes] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const inputRefs = useRef({});

    // Estados para el nuevo flujo de ventas
    const [tipoCliente, setTipoCliente] = useState("consumidor_final"); 
    const [tipoVenta, setTipoVenta] = useState("consumo_establecimiento"); 
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [productosSeleccionados, setProductosSeleccionados] = useState([]);
    const [pedidos, setPedidos] = useState([]);
    const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
    const [nuevoCliente, setNuevoCliente] = useState({
        nombre: "",
        correo: "",
        telefono: "",
        tipo_identificacion: "05",
        identificacion: "",
        direccion: "",
        tipo_cliente: "invitado",
        id_restaurante: "",
    });
    const [mostrarFormCliente, setMostrarFormCliente] = useState(false);
    const [busquedaCliente, setBusquedaCliente] = useState("");
    const [busquedaProducto, setBusquedaProducto] = useState("");
    const [generandoFactura, setGenerandoFactura] = useState(false); // Estado de carga para facturación

    const [factura, setFactura] = useState({
        establecimientoId: "",
        metodo_pago: "efectivo",
        // El ambiente se obtiene automáticamente del restaurante
    });

    const { sortedItems: sortedVentas, sortConfig, setSortConfig } = useSortableData(filteredVentas);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentVentas, totalPages, itemsPerPage } = usePagination(sortedVentas, 10);

    // 🔹 Cargar clientes
    const loadClientes = async () => {
        try {
            const data = await getClientes();
            setClientes(Array.isArray(data) ? data : data.clientes || []);
        } catch (err) {
            console.error("Error al cargar clientes:", err);
        }
    };

    useEffect(() => {
        loadClientes();
    }, []);

    // 🔹 Cargar productos
    useEffect(() => {
        const loadProductos = async () => {
            try {
                const data = await getProductos();
                setProductos(Array.isArray(data) ? data : data.productos || []);
            } catch (err) {
                console.error("Error al cargar productos:", err);
            }
        };
        loadProductos();
    }, []);

    // 🔹 Cargar pedidos
    useEffect(() => {
        const loadPedidos = async () => {
            try {
                const data = await getPedidos();
                setPedidos(Array.isArray(data) ? data : data.pedidos || []);
            } catch (err) {
                console.error("Error al cargar pedidos:", err);
            }
        };
        loadPedidos();
    }, []);

    // 🔹 Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
            try {
                const data = await getRestaurantes();
                setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
            } catch (err) {
                console.error("Error al cargar restaurantes:", err);
            }
        };
        loadRestaurantes();
    }, []);

    // 🔹 Filtrar clientes según búsqueda
    const clientesFiltrados = clientes.filter(c => 
        c.nombre?.toLowerCase().includes(busquedaCliente.toLowerCase()) ||
        c.identificacion?.includes(busquedaCliente) ||
        c.correo?.toLowerCase().includes(busquedaCliente.toLowerCase())
    );

    // 🔹 Filtrar productos según búsqueda
    const productosFiltrados = productos.filter(p =>
        p.nombre?.toLowerCase().includes(busquedaProducto.toLowerCase()) ||
        p.categoria?.nombre?.toLowerCase().includes(busquedaProducto.toLowerCase())
    );

    // 🔹 Filtrar pedidos sin facturar
    const pedidosSinFacturar = pedidos.filter(p => 
        p.estado === 'completado' && !p.facturado
    );

    // 🔹 Cargar establecimientos
    useEffect(() => {
        const loadEstablecimientos = async () => {
            try {
                const data = await getEstablecimientos();
                setEstablecimientos(Array.isArray(data) ? data : data.establecimientos || []);
            } catch (err) {
                console.error("Error al cargar establecimientos:", err);
            }
        };
        loadEstablecimientos();
    }, []);

    // 🔹 Cargar ventas
    const loadVentas = async () => {
        try {
            const data = await getVentas();
            const lista = Array.isArray(data) ? data : data.ventas || [];
            setVentas(lista);
            setFilteredVentas(lista);
        } catch (err) {
            console.error("Error al cargar ventas:", err);
        }
    };

    useEffect(() => {
        loadVentas();
    }, []);

    //  Filtro de búsqueda
    useEffect(() => {
        const filtered = ventas.filter((v) =>
            Object.values(v).some((val) =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredVentas(filtered);
    }, [searchTerm, ventas]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFactura({ ...factura, [name]: value });
    };

    const handleClienteChange = (e) => {
        const { name, value } = e.target;
        setNuevoCliente({ ...nuevoCliente, [name]: value });
        
        // Limpiar error específico del campo que se está editando
        if (errors[name]) {
            setErrors({ ...errors, [name]: undefined });
        }
    };

    const seleccionarCliente = (cliente) => {
        setClienteSeleccionado(cliente);
        setBusquedaCliente("");
        setMostrarFormCliente(false);
        setErrors({}); // Limpiar errores al seleccionar cliente
    };

    const agregarProducto = (producto) => {
        const productoExistente = productosSeleccionados.find(p => p.id_producto === producto.id_producto);
        
        if (productoExistente) {
            setProductosSeleccionados(productosSeleccionados.map(p => 
                p.id_producto === producto.id_producto 
                    ? { ...p, cantidad: p.cantidad + 1 }
                    : p
            ));
        } else {
            setProductosSeleccionados([...productosSeleccionados, {
                ...producto,
                cantidad: 1,
                precio_unitario: producto.precio || 0
            }]);
        }
    };

    const actualizarCantidadProducto = (id_producto, cantidad) => {
        if (cantidad <= 0) {
            setProductosSeleccionados(productosSeleccionados.filter(p => p.id_producto !== id_producto));
        } else {
            setProductosSeleccionados(productosSeleccionados.map(p => 
                p.id_producto === id_producto ? { ...p, cantidad } : p
            ));
        }
    };

    const calcularSubtotal = () => {
        return productosSeleccionados.reduce((total, producto) => {
            return total + (producto.cantidad * producto.precio_unitario);
        }, 0);
    };

    const calcularImpuestos = () => {
        // Obtener el IVA vigente del restaurante del establecimiento seleccionado
        const establecimientoActual = establecimientos.find(e => e.id_establecimiento === parseInt(factura.establecimientoId));
        const ivaVigente = establecimientoActual?.restaurante?.iva_vigente || 0; // Por defecto 0% para restaurantes
        
        return productosSeleccionados.reduce((total, producto) => {
            const subtotalProducto = producto.cantidad * producto.precio_unitario;
            
            // Calcular IVA según el código del producto (Tabla SRI Ecuador 2025)
            let tarifaIVA = 0;
            switch (producto.iva_codigo) {
                case '0':  tarifaIVA = 0;    break; // IVA 0%
                case '2':  tarifaIVA = 0.15; break; // IVA 15% (vigente desde 2025)
                case '3':  tarifaIVA = 0.14; break; // IVA 14%
                case '4':  tarifaIVA = 0.15; break; // IVA 15%
                case '5':  tarifaIVA = 0.05; break; // IVA 5%
                case '6':  tarifaIVA = 0;    break; // No Objeto
                case '7':  tarifaIVA = 0;    break; // Exento
                case '8':  tarifaIVA = ivaVigente / 100; break; // IVA diferenciado (usa el del restaurante)
                case '10': tarifaIVA = 0.13; break; // IVA 13%
                default:   tarifaIVA = 0;    break;
            }
            
            return total + (subtotalProducto * tarifaIVA);
        }, 0);
    };

    const calcularTotal = () => {
        return calcularSubtotal() + calcularImpuestos();
    };

    const validateCliente = (data) => {
        const newErrors = {};
        if (!data.nombre.trim()) newErrors.nombre = "El nombre es obligatorio";
        if (!data.direccion.trim()) newErrors.direccion = "La dirección es obligatoria.";
        if (!data.identificacion.trim()) newErrors.identificacion = "La identificación es obligatoria";
        if (!data.tipo_identificacion) newErrors.tipo_identificacion = "Seleccione tipo de identificación";
        if (!data.id_restaurante) newErrors.id_restaurante = "Seleccione un restaurante";
        if (!data.telefono.trim()) newErrors.telefono = "El Teléfono es obligatorio.";
        else if (!/^\d{10}$/.test(data.telefono)) newErrors.telefono = "Debe tener 10 dígitos.";
        if (!data.correo.trim()) { newErrors.correo = "El correo es obligatorio."; } 
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo.trim())) { newErrors.correo = "El formato del correo no es válido."; }
        return newErrors;
    };

    const crearCliente = async () => {
        const validationErrors = validateCliente(nuevoCliente);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const usuarioLogueado = JSON.parse(localStorage.getItem("usuario"));
            const clienteConUsuario = {
                ...nuevoCliente,
                id_usuario: usuarioLogueado?.id_usuario || null,
            };

            const clienteCreado = await createCliente(clienteConUsuario);
            
            // 🔹 Actualizar la lista de clientes inmediatamente
            const nuevaListaClientes = [...clientes, clienteCreado];
            setClientes(nuevaListaClientes);
            
            // 🔹 Seleccionar automáticamente el cliente recién creado
            setClienteSeleccionado(clienteCreado);
            
            // 🔹 Limpiar formulario y cerrar modal de cliente
            setNuevoCliente({
                nombre: "",
                correo: "",
                telefono: "",
                tipo_identificacion: "05",
                identificacion: "",
                direccion: "",
                tipo_cliente: "invitado",
                id_restaurante: "",
            });
            setMostrarFormCliente(false);
            setBusquedaCliente(""); // Limpiar búsqueda
            setErrors({}); // Limpiar errores
            
            alert("Cliente creado y seleccionado exitosamente");
        } catch (err) {
            console.error("Error al crear cliente:", err);
            
            // Captura tanto el mensaje general como el error específico (igual que en ClientesPage)
            const backendMsg = err.response?.data
                ? `${err.response.data.message}: ${err.response.data.error}`
                : "Error al crear el cliente";
            setErrors({ general: backendMsg });
            alert(backendMsg);
        }
    };

    const resetForm = () => {
        setFactura({
            establecimientoId: "",
            metodo_pago: "efectivo",
            // El ambiente se obtiene automáticamente del restaurante
        });
        setTipoCliente("consumidor_final");
        setTipoVenta("consumo_establecimiento");
        setClienteSeleccionado(null);
        setProductosSeleccionados([]);
        setPedidoSeleccionado(null);
        setBusquedaCliente("");
        setBusquedaProducto("");
        setMostrarFormCliente(false);
        setNuevoCliente({
            nombre: "",
            correo: "",
            telefono: "",
            tipo_identificacion: "05",
            identificacion: "",
            direccion: "",
            tipo_cliente: "invitado",
            id_restaurante: "",
        });
        setErrors({});
        setEditMode(false);
        setVentaCreada(null); // Limpiar venta creada
    };

    const validateVenta = () => {
        const newErrors = {};
        
        // Validar cliente solo si es "con_datos"
        if (tipoCliente === "con_datos" && !clienteSeleccionado) {
            newErrors.cliente = "Seleccione o registre un cliente.";
        }
        
        // Validar según tipo de venta
        if (tipoVenta === "consumo_establecimiento") {
            if (productosSeleccionados.length === 0) newErrors.productos = "Agregue al menos un producto.";
        } else {
            if (!pedidoSeleccionado) newErrors.pedido = "Seleccione un pedido.";
        }
        
        if (!factura.establecimientoId) newErrors.establecimientoId = "Seleccione un punto de emisión.";
        if (!factura.metodo_pago) newErrors.metodo_pago = "Seleccione un método de pago.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateVenta();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            let ventaData;

            if (tipoVenta === "consumo_establecimiento") {
                // Venta por consumo directo en establecimiento
                // El backend calculará subtotal, impuestos y total automáticamente
                ventaData = {
                    tipo: "consumo_establecimiento",
                    tipoCliente: tipoCliente,
                    cliente: tipoCliente === "con_datos" ? clienteSeleccionado : null,
                    productos: productosSeleccionados,
                    establecimientoId: factura.establecimientoId,
                    metodo_pago: factura.metodo_pago
                };
            } else {
                // Venta por pedido/delivery existente
                ventaData = {
                    tipo: "pedidos_delivery",
                    tipoCliente: tipoCliente,
                    id_pedido: pedidoSeleccionado.id_pedido,
                    establecimientoId: factura.establecimientoId,
                    metodo_pago: factura.metodo_pago,
                };
            }

            const venta = await createVenta(ventaData);
            setVentaCreada(venta);
            alert("¡Venta registrada exitosamente!");
            
            // Si es consumidor final, cerrar modal automáticamente
            if (tipoCliente === "consumidor_final") {
                resetForm();
                setModalOpen(false);
                loadVentas();
            }
            // Si es con datos, mantener modal abierto para generar factura electrónica
        } catch (err) {
            console.error("Error del backend:", err.response?.data || err.message);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al procesar la venta.";
            setErrors({ general: backendMsg });
            alert(backendMsg);
        }
    };

    const handleGenerarFactura = async () => {
        if (!ventaCreada) {
            alert("Primero debe registrar la venta");
            return;
        }

        // Validar que sea una venta con datos de cliente
        if (tipoCliente !== "con_datos") {
            alert("Solo se pueden facturar ventas con datos de cliente");
            return;
        }

        setGenerandoFactura(true); // Activar indicador de carga
        
        try {
            const facturaData = {
                id_venta: ventaCreada.id_venta,
                establecimientoId: factura.establecimientoId,
                metodo_pago: factura.metodo_pago,
                // El ambiente se obtiene automáticamente del restaurante en el backend
            };

            await createFactura(facturaData);
            
            // Desactivar spinner ANTES de mostrar el mensaje
            setGenerandoFactura(false);
            
            // Pequeño delay para que el usuario vea que terminó el proceso
            await new Promise(resolve => setTimeout(resolve, 100));
            
            alert("¡Factura electrónica generada, autorizada por el SRI y enviada al correo exitosamente!");
            resetForm();
            setModalOpen(false);
            loadVentas();
        } catch (err) {
            // Desactivar spinner ANTES de mostrar el error
            setGenerandoFactura(false);
            
            // Pequeño delay para que el usuario vea que terminó el proceso
            await new Promise(resolve => setTimeout(resolve, 100));
            
            console.error("Error al generar factura:", err.response?.data || err.message);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al generar la factura electrónica.";
            alert(backendMsg);
        }
    };

    const handleEdit = (v) => {
        // Por ahora no permitimos editar ventas
        alert("La edición de ventas no está disponible");
    };

    const handleAnular = async (id) => {
        if (window.confirm("¿Seguro que desea anular esta venta?")) {
            try {
                await anularVenta(id);
                loadVentas();
                alert("Venta anulada correctamente");
            } catch (err) {
                console.error("Error al anular venta:", err);
                const backendMsg = err.response?.data?.error || err.response?.data?.message || "Error al anular la venta";
                alert(backendMsg);
            }
        }
    };

    const handleImprimir = (venta) => {
        setVentaParaImprimir(venta);
    };

    const handleCerrarModal = () => {
        // Si hay una venta creada, recargar la lista antes de cerrar
        if (ventaCreada) {
            loadVentas();
        }
        resetForm();
        setModalOpen(false);
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">Sistema de Ventas</h1>

            {/* 🔍 Buscador + Controles */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <input
                    type="text"
                    placeholder="🔍 Buscar ventas por número, cliente o fecha..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border p-2 rounded w-full md:w-96"
                />
                <button
                    onClick={() => {
                        resetForm();
                        setModalOpen(true);
                    }}
                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 whitespace-nowrap flex items-center gap-2"
                >
                    <span className="text-xl">🧾</span>
                    Nueva Venta
                </button>
            </div>

            {/* Tabla de Ventas */}
            <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
                <table className="min-w-full text-sm text-left divide-y divide-gray-200">
                    <thead className="bg-gray-100 text-gray-700 font-semibold">
                        <tr>
                            <SortableHeader label="N°" sortKey="id_venta" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <SortableHeader label="Tipo" sortKey="tipo_venta" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <SortableHeader label="Cliente" sortKey="tipo_cliente" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <th className="px-3 py-2">Productos</th>
                            <SortableHeader label="Subtotal" sortKey="subtotal" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <SortableHeader label="IVA" sortKey="impuestos" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <SortableHeader label="Total" sortKey="total" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            <SortableHeader label="Fecha" sortKey="createdAt" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            {usuarioLogueado?.isSuperAdmin && (
                                <SortableHeader label="Restaurante" sortKey="establecimiento.restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-3 py-2" />
                            )}
                            <th className="px-3 py-2">Estado</th>
                            <th className="px-3 py-2 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentVentas.length > 0 ? (
                            currentVentas.map((v, idx) => (
                                <tr key={v.id_venta} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 font-medium">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                    <td className="px-3 py-2"> <span className={`px-2 py-1 rounded text-xs font-medium ${ v.tipo_venta === "consumo_establecimiento" ? "bg-purple-100 text-purple-800" : "bg-orange-100 text-orange-800" }`}> {v.tipo_venta === "consumo_establecimiento" ? "Consumo" : "Delivery"} </span> </td>
                                    <td className="px-3 py-2"> <div className="max-w-xs truncate" title={v.tipo_cliente === "consumidor_final" ? "Consumidor Final" : v.cliente?.nombre}> {v.tipo_cliente === "consumidor_final" ? <span className="text-gray-500 italic">Consumidor Final</span> : <span className="font-medium">{v.cliente?.nombre || "—"}</span> } </div> </td>
                                    <td className="px-3 py-2">
                                        <div className="text-gray-600">
                                            {v.detalles && v.detalles.length > 0 ? (
                                                <div className="max-w-xs">
                                                    {v.detalles.slice(0, 2).map((d, i) => (
                                                        <div key={i} className="truncate">
                                                            • {d.producto?.nombre || "—"} ({d.cantidad})
                                                        </div>
                                                    ))}
                                                    {v.detalles.length > 2 && (
                                                        <div className="text-blue-600 font-medium">
                                                            +{v.detalles.length - 2} más
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">Sin productos</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 font-medium"> ${v.subtotal ? Number(v.subtotal).toFixed(2) : "0.00"} </td>
                                    <td className="px-3 py-2 text-gray-600"> ${v.impuestos ? Number(v.impuestos).toFixed(2) : "0.00"} </td>
                                    <td className="px-3 py-2 font-bold text-green-700"> ${v.total ? Number(v.total).toFixed(2) : "0.00"} </td>
                                    <td className="px-3 py-2 text-xs"> {new Date(v.createdAt).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })} <div className="text-gray-500"> {new Date(v.createdAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })} </div> </td>
                                    {usuarioLogueado?.isSuperAdmin && (
                                        <td className="px-3 py-2">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                                {v.establecimiento?.restaurante?.nombre_comercial || "Sin restaurante"}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-3 py-2"> <span className={`px-2 py-1 rounded text-xs font-medium ${ v.estado === "registrada" ? "bg-green-100 text-green-800" : v.estado === "facturada" ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800" }`} > {v.estado} </span> </td>
                                    <td className="px-3 py-2">
                                        <div className="flex justify-center gap-2">
                                            <button 
                                                className="bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 text-xs font-medium flex items-center gap-1.5 transition-colors shadow-sm" 
                                                onClick={() => handleImprimir(v)} 
                                                title="Imprimir comprobante"
                                            >
                                                <span className="text-sm">🖨️</span>
                                                Imprimir
                                            </button>
                                            <button 
                                                className="bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs font-medium flex items-center gap-1.5 transition-colors shadow-sm" 
                                                onClick={() => handleAnular(v.id_venta)} 
                                                disabled={v.estado === "anulada"} 
                                                title="Anular venta"
                                            >
                                                <span className="text-sm">✖</span>
                                                Anular
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={usuarioLogueado?.isSuperAdmin ? "11" : "10"} className="text-center py-4 text-gray-500">
                                    No hay ventas registradas
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* Modal Crear / Editar */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-lg w-[80%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                        <button
                            onClick={handleCerrarModal}
                            className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"
                        >
                            ✖
                        </button>
                        <h2 className="text-2xl font-semibold mb-6 text-center">
                            {editMode ? "Editar Venta" : "Registrar Nueva Venta"}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Configuración de Venta */}
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-center font-semibold text-purple-800 mb-4">Configuración de Venta</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputGroup label="Tipo de Cliente (SRI)" error={errors.tipoCliente}>
                                        <select
                                            name="tipoCliente"
                                            value={tipoCliente}
                                            onChange={(e) => setTipoCliente(e.target.value)}
                                            className="input-base"
                                        >
                                            <option value="consumidor_final">Consumidor Final</option>
                                            <option value="con_datos">Factura con Datos del Cliente</option>
                                        </select>
                                        <small className="text-gray-600 mt-1 block">
                                            {tipoCliente === "consumidor_final" 
                                                ? "No requiere datos específicos del cliente (RUC: 9999999999999)"
                                                : "Requiere datos completos del cliente para facturación"
                                            }
                                        </small>
                                    </InputGroup>

                                    <InputGroup label="Tipo de Venta" error={errors.tipoVenta}>
                                        <select
                                            name="tipoVenta"
                                            value={tipoVenta}
                                            onChange={(e) => setTipoVenta(e.target.value)}
                                            className="input-base"
                                        >
                                            <option value="consumo_establecimiento">Consumo en Establecimiento</option>
                                            <option value="pedidos_delivery">Pedidos / Delivery</option>
                                        </select>
                                        <small className="text-gray-600 mt-1 block">
                                            {tipoVenta === "consumo_establecimiento"
                                                ? "Cliente consume directamente en el local"
                                                : "Pedidos para llevar o entrega a domicilio"
                                            }
                                        </small>
                                    </InputGroup>
                                </div>
                            </div>
                            {/* PASO 1: Cliente (condicional según tipo) */}
                            {tipoCliente === "con_datos" && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="font-semibold text-blue-800 mb-3">Datos del Cliente</h3>
                                    
                                    {!clienteSeleccionado ? (
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                placeholder="🔍 Buscar cliente por nombre, cédula o correo..."
                                                value={busquedaCliente}
                                                onChange={(e) => setBusquedaCliente(e.target.value)}
                                                className="flex-1 border p-2 rounded"
                                            />
                                            <button
                                                type="button"
                                                onClick={loadClientes}
                                                className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                                                title="Actualizar lista de clientes"
                                            >
                                                🔄
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setMostrarFormCliente(true)}
                                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                            >
                                                ➕ Nuevo Cliente
                                            </button>
                                        </div>
                                        
                                        {busquedaCliente && (
                                            <div className="max-h-32 overflow-y-auto border rounded">
                                                {clientesFiltrados.map(cliente => (
                                                    <div
                                                        key={cliente.id_cliente}
                                                        onClick={() => seleccionarCliente(cliente)}
                                                        className="p-2 hover:bg-gray-100 cursor-pointer border-b"
                                                    >
                                                        <div className="font-medium">{cliente.nombre}</div>
                                                        <div className="text-sm text-gray-600">{cliente.identificacion} - {cliente.correo}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {mostrarFormCliente && (
                                            <div className="bg-white p-4 rounded border">
                                                <h4 className="font-medium mb-3">Registrar Nuevo Cliente</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <InputGroup label="Nombre" error={errors.nombre}>
                                                        <input
                                                            name="nombre"
                                                            value={nuevoCliente.nombre}
                                                            onChange={handleClienteChange}
                                                            placeholder="Ingrese nombre completo"
                                                            className="input-base"
                                                        />
                                                    </InputGroup>
                                                    
                                                    <InputGroup label="Dirección" error={errors.direccion}>
                                                        <input
                                                            name="direccion"
                                                            value={nuevoCliente.direccion}
                                                            onChange={handleClienteChange}
                                                            placeholder="Ingrese dirección"
                                                            className="input-base"
                                                        />
                                                    </InputGroup>

                                                    <InputGroup label="Tipo Identificación" error={errors.tipo_identificacion}>
                                                        <select
                                                            name="tipo_identificacion"
                                                            value={nuevoCliente.tipo_identificacion}
                                                            onChange={handleClienteChange}
                                                            className="input-base"
                                                        >
                                                            <option value="05">Cédula</option>
                                                            <option value="04">RUC</option>
                                                            <option value="06">Pasaporte</option>
                                                        </select>
                                                    </InputGroup>

                                                    <InputGroup label="Identificación" error={errors.identificacion}>
                                                        <input
                                                            name="identificacion"
                                                            value={nuevoCliente.identificacion}
                                                            onChange={handleClienteChange}
                                                            placeholder="Ingrese número de identificación"
                                                            className="input-base"
                                                        />
                                                    </InputGroup>

                                                    <InputGroup label="Teléfono" error={errors.telefono}>
                                                        <input
                                                            name="telefono"
                                                            value={nuevoCliente.telefono}
                                                            onChange={handleClienteChange}
                                                            placeholder="Ingrese teléfono"
                                                            className="input-base"
                                                        />
                                                    </InputGroup>

                                                    <InputGroup label="Correo" error={errors.correo}>
                                                        <input
                                                            name="correo"
                                                            value={nuevoCliente.correo}
                                                            onChange={handleClienteChange}
                                                            placeholder="correo@ejemplo.com"
                                                            className="input-base"
                                                        />
                                                    </InputGroup>

                                                    <InputGroup label="Restaurante" error={errors.id_restaurante}>
                                                        <select
                                                            name="id_restaurante"
                                                            value={nuevoCliente.id_restaurante}
                                                            onChange={handleClienteChange}
                                                            className="input-base"
                                                        >
                                                            <option value="">Seleccione un restaurante</option>
                                                            {restaurantes.map(r => (
                                                                <option key={r.id_restaurante} value={r.id_restaurante}>
                                                                    {r.nombre_comercial}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </InputGroup>

                                                    <InputGroup label="Tipo Cliente" error={errors.tipo_cliente}>
                                                        <select
                                                            name="tipo_cliente"
                                                            value={nuevoCliente.tipo_cliente}
                                                            onChange={handleClienteChange}
                                                            className="input-base"
                                                        >
                                                            <option value="registrado">Registrado</option>
                                                            <option value="invitado">Invitado</option>
                                                        </select>
                                                    </InputGroup>
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    <button
                                                        type="button"
                                                        onClick={crearCliente}
                                                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                                                    >
                                                        Guardar Cliente
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setMostrarFormCliente(false);
                                                            setErrors({});
                                                            setNuevoCliente({
                                                                nombre: "",
                                                                correo: "",
                                                                telefono: "",
                                                                tipo_identificacion: "05",
                                                                identificacion: "",
                                                                direccion: "",
                                                                tipo_cliente: "invitado",
                                                                id_restaurante: "",
                                                            });
                                                        }}
                                                        className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {errors.cliente && <p className="text-red-600 text-sm">{errors.cliente}</p>}
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center bg-white p-3 rounded border">
                                        <div>
                                            <div className="font-medium">{clienteSeleccionado.nombre}</div>
                                            <div className="text-sm text-gray-600">{clienteSeleccionado.identificacion} - {clienteSeleccionado.correo}</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setClienteSeleccionado(null)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            ✖ Cambiar
                                        </button>
                                    </div>
                                )}
                                </div>
                            )}

                            {/* PASO 2: Contenido según tipo de venta */}
                            {tipoVenta === "consumo_establecimiento" ? (
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <h3 className="text-center font-semibold text-green-800 mb-3">Productos Consumidos</h3>
                                    
                                    {/* Buscador de productos */}
                                    <div className="mb-4">
                                        <input
                                            type="text"
                                            placeholder="🔍 Buscar productos por nombre o categoría..."
                                            value={busquedaProducto}
                                            onChange={(e) => setBusquedaProducto(e.target.value)}
                                            className="w-full border p-2 rounded"
                                        />
                                    </div>

                                    {/* Listbox de productos */}
                                    <div className="bg-white border rounded max-h-48 overflow-y-auto mb-4">
                                        {productosFiltrados.map(producto => (
                                            <div
                                                key={producto.id_producto}
                                                onClick={() => agregarProducto(producto)}
                                                className="p-3 border-b hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                            >
                                                <div>
                                                    <div className="font-medium">{producto.nombre}</div>
                                                    <div className="text-sm text-gray-600">{producto.categoria?.nombre}</div>
                                                </div>
                                                <div className="text-green-600 font-bold">${Number(producto.precio || 0).toFixed(2)}</div>
                                            </div>
                                        ))}
                                        {productosFiltrados.length === 0 && busquedaProducto && (
                                            <div className="p-3 text-gray-500 text-center">No se encontraron productos</div>
                                        )}
                                    </div>

                                    {/* Productos Seleccionados */}
                                    {productosSeleccionados.length > 0 && (
                                        <div className="bg-white p-4 rounded border">
                                            <h4 className="font-medium mb-3">Productos Seleccionados:</h4>
                                            {productosSeleccionados.map(producto => (
                                                <div key={producto.id_producto} className="flex justify-between items-center py-2 border-b">
                                                    <div className="flex-1">
                                                        <div className="font-medium">{producto.nombre}</div>
                                                        <div className="text-sm text-gray-600">${Number(producto.precio_unitario).toFixed(2)} c/u</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => actualizarCantidadProducto(producto.id_producto, producto.cantidad - 1)}
                                                            className="bg-red-500 text-white w-8 h-8 rounded hover:bg-red-600"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="w-8 text-center">{producto.cantidad}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => actualizarCantidadProducto(producto.id_producto, producto.cantidad + 1)}
                                                            className="bg-green-500 text-white w-8 h-8 rounded hover:bg-green-600"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                    <div className="w-20 text-right font-bold">
                                                        ${(producto.cantidad * producto.precio_unitario).toFixed(2)}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="mt-3 pt-3 border-t">
                                                <div className="flex justify-between">
                                                    <span>Subtotal:</span>
                                                    <span>${calcularSubtotal().toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>IVA:</span>
                                                    <span>${calcularImpuestos().toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between font-bold text-lg">
                                                    <span>Total:</span>
                                                    <span>${calcularTotal().toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {errors.productos && <p className="text-red-600 text-sm">{errors.productos}</p>}
                                </div>
                            ) : (
                                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                                    <h3 className="font-semibold text-orange-800 mb-3">Pedidos / Delivery</h3>
                                    
                                    <select
                                        value={pedidoSeleccionado?.id_pedido || ""}
                                        onChange={(e) => {
                                            const pedido = pedidosSinFacturar.find(p => p.id_pedido == e.target.value);
                                            setPedidoSeleccionado(pedido);
                                        }}
                                        className="w-full border p-2 rounded"
                                    >
                                        <option value="">-- Seleccione un pedido para facturar --</option>
                                        {pedidosSinFacturar.map(pedido => (
                                            <option key={pedido.id_pedido} value={pedido.id_pedido}>
                                                Pedido #{pedido.id_pedido} - {pedido.cliente?.nombre || "Sin cliente"} - ${Number(pedido.total || 0).toFixed(2)}
                                            </option>
                                        ))}
                                    </select>

                                    {pedidoSeleccionado && (
                                        <div className="mt-4 bg-white p-4 rounded border">
                                            <h4 className="font-medium mb-2">Detalles del Pedido:</h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div><strong>Cliente:</strong> {pedidoSeleccionado.cliente?.nombre || "Sin cliente"}</div>
                                                <div><strong>Fecha:</strong> {new Date(pedidoSeleccionado.fecha_pedido || pedidoSeleccionado.createdAt).toLocaleDateString()}</div>
                                                <div><strong>Estado:</strong> {pedidoSeleccionado.estado}</div>
                                                <div><strong>Total:</strong> ${Number(pedidoSeleccionado.total || 0).toFixed(2)}</div>
                                            </div>
                                        </div>
                                    )}

                                    {errors.pedido && <p className="text-red-600 text-sm">{errors.pedido}</p>}
                                </div>
                            )}

                            {/* PASO 3: Configuración de Factura */}
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                <h3 className="font-semibold text-yellow-800 mb-3">Configuración</h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputGroup label="Punto de Emisión" error={errors.establecimientoId}>
                                        <select
                                            name="establecimientoId"
                                            value={factura.establecimientoId}
                                            onChange={handleChange}
                                            className="input-base"
                                        >
                                            <option value="">-- Seleccione punto de emisión --</option>
                                            {establecimientos.map((e) => (
                                                <option key={e.id || e.id_establecimiento} value={e.id || e.id_establecimiento}>
                                                    📍 {e.estab}-{e.ptoemi} | {e.direstablecimiento}
                                                </option>
                                            ))}
                                        </select>
                                    </InputGroup>

                                    <InputGroup label="Método de Pago" error={errors.metodo_pago}>
                                        <select
                                            name="metodo_pago"
                                            value={factura.metodo_pago}
                                            onChange={handleChange}
                                            className="input-base"
                                        >
                                            <option value="efectivo">💵 Efectivo</option>
                                            <option value="tarjeta">💳 Tarjeta (Crédito/Débito)</option>
                                            <option value="transferencia">🏦 Transferencia Bancaria</option>
                                            <option value="cheque">📝 Cheque</option>
                                        </select>
                                    </InputGroup>
                                </div>
                            </div>

                            {errors.general && (
                                <p className="text-red-600 text-sm text-center">{errors.general}</p>
                            )}

                            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
                                <div className="text-center space-y-3">
                                    {!ventaCreada ? (
                                        <>
                                            <div className="flex items-center justify-center gap-2 text-green-700">
                                                <span className="text-2xl">💰</span>
                                                <span className="font-semibold">Registrar Venta</span>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                Se registrará la venta en el sistema
                                            </p>
                                            <button
                                                type="submit"
                                                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg"
                                            >
                                                Registrar Venta
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="bg-green-100 p-4 rounded-lg mb-4">
                                                <p className="text-green-800 font-semibold">Venta registrada exitosamente</p>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    Total: ${ventaCreada.total ? Number(ventaCreada.total).toFixed(2) : "0.00"}
                                                </p>
                                            </div>
                                            
                                            {tipoCliente === "con_datos" && (
                                                <>
                                                    <div className="flex items-center justify-center gap-2 text-blue-700">
                                                        <span className="text-2xl">🧾</span>
                                                        <span className="font-semibold">Generar Factura Electrónica</span>
                                                    </div>
                                                    <p className="text-sm text-gray-600">
                                                        Se generará y enviará la factura electrónica al SRI
                                                    </p>
                                                    
                                                    {generandoFactura && (
                                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                                            <div className="flex items-center justify-center gap-3">
                                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                                <div className="text-left">
                                                                    <p className="text-blue-800 font-semibold">Generando factura electrónica...</p>
                                                                    <p className="text-blue-600 text-sm">
                                                                        Firmando XML → Enviando al SRI → Generando PDF → Enviando email
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex gap-3 justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={handleGenerarFactura}
                                                            disabled={generandoFactura}
                                                            className={`px-8 py-3 rounded-lg font-semibold transition-colors shadow-lg flex items-center gap-2 ${
                                                                generandoFactura 
                                                                    ? 'bg-gray-400 cursor-not-allowed' 
                                                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                                            }`}
                                                        >
                                                            {generandoFactura ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                                    <span>Procesando...</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span>📄</span>
                                                                    <span>Generar Factura Electrónica</span>
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleCerrarModal}
                                                            disabled={generandoFactura}
                                                            className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                                                                generandoFactura
                                                                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                                                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                                                            }`}
                                                        >
                                                            Omitir Factura
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Impresión */}
            {ventaParaImprimir && (
                <ComprobanteVenta
                    venta={ventaParaImprimir}
                    onClose={() => setVentaParaImprimir(null)}
                />
            )}
        </div>
    );
};

export default FacturaPage;
